// src/game/Game.jsx
// ============================================
// Quiz of Kings - نسخه نهایی کامل
// ============================================

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useCoins } from '../context/CoinContext';
import gameAPI from './gameAPI';
import './game.css';

// ============================================
// ۱. ثابت‌ها
// ============================================

const VIEWS = {
  HOME: 'home',
  WAITING: 'waiting',
  TOPIC_SELECT: 'topic_select',
  PLAYING: 'playing',
  QUESTION_RESULT: 'question_result',
  FINISHED: 'finished',
};

const CATEGORIES = [
  'اطلاعات عمومی', 'ورزش', 'لوگو و سرگرمی', 'مذهبی', 'جغرافیا',
  'تاریخ', 'علم و دانش', 'زبان انگلیسی', 'غذا و نوشیدنی', 'فرهنگ',
  'ریاضی و هوش', 'فوتبال', 'سینما', 'موسیقی', 'برنامه‌های تلویزیونی',
  'ادبیات', 'هنر', 'طبیعت و جانداران', 'بازی‌های ویدیویی'
];

const TOTAL_STAGES = 6;
const QUESTIONS_PER_STAGE = 3;
const TOTAL_QUESTIONS = TOTAL_STAGES * QUESTIONS_PER_STAGE;
const QUESTION_TIMER = 15;

const HELP_COSTS = {
  remove_two: 40,
  ask_audience: 60,
  renewed_chance: 40,
};

// ============================================
// ۲. کامپوننت اصلی
// ============================================

const Game = () => {
  const { user } = useAuth();
  const { coins, xp, addRewards } = useCoins();
  
  // ===== Stateهای اصلی =====
  const [view, setView] = useState(VIEWS.HOME);
  const [mode, setMode] = useState('random');
  const [players, setPlayers] = useState([]);
  const [currentStage, setCurrentStage] = useState(0);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [timer, setTimer] = useState(QUESTION_TIMER);
  const [score, setScore] = useState(0);
  const [opponentScore, setOpponentScore] = useState(0);
  const [isAnswered, setIsAnswered] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [temporaryCategories, setTemporaryCategories] = useState([]);
  const [helpUsed, setHelpUsed] = useState({ remove_two: false, ask_audience: false, renewed_chance: false });
  const [gameResult, setGameResult] = useState(null);
  const [recentGames, setRecentGames] = useState([]);
  const [error, setError] = useState(null);
  const [waitingOpponent, setWaitingOpponent] = useState(null);
  const [questionResult, setQuestionResult] = useState(null);
  const [myPlayerId, setMyPlayerId] = useState(null);

  // ===== Refs =====
  const isMounted = useRef(true);
  const timerIntervalRef = useRef(null);

  // ============================================
  // ۳. بارگذاری بازی‌های اخیر
  // ============================================
  
  useEffect(() => {
    const saved = localStorage.getItem('recentGames');
    if (saved) {
      try {
        setRecentGames(JSON.parse(saved));
      } catch {}
    }
    return () => { isMounted.current = false; };
  }, []);

  // ============================================
  // ۴. ذخیره بازی اخیر
  // ============================================
  
  const saveRecentGame = (gameData) => {
    const newList = [gameData, ...recentGames.slice(0, 2)];
    setRecentGames(newList);
    localStorage.setItem('recentGames', JSON.stringify(newList));
  };

  // ============================================
  // ۵. تایمر در کلاینت
  // ============================================
  
  useEffect(() => {
    if (view === VIEWS.PLAYING && !isAnswered && timer > 0) {
      timerIntervalRef.current = setInterval(() => {
        setTimer(prev => {
          if (prev <= 1) {
            clearInterval(timerIntervalRef.current);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } else {
      clearInterval(timerIntervalRef.current);
    }
    return () => clearInterval(timerIntervalRef.current);
  }, [view, isAnswered, timer]);

  // ============================================
  // ۶. شروع بازی
  // ============================================
  
  const startGame = async (selectedMode = 'random') => {
    setError(null);
    setView(VIEWS.WAITING);
    setMode(selectedMode);
    setWaitingOpponent(null);
    
    try {
      const result = await gameAPI.connectToServer(user?.username || 'کاربر', selectedMode);
      if (!result.success) {
        setError(result.error);
        setView(VIEWS.HOME);
        return;
      }
      
      // تنظیم شنونده‌های رویداد
      gameAPI.on('game_started', handleGameStarted);
      gameAPI.on('opponent_found', handleOpponentFound);
      gameAPI.on('new_question', handleNewQuestion);
      gameAPI.on('timer_update', handleTimerUpdate);
      gameAPI.on('answer_result', handleAnswerResult);
      gameAPI.on('player_answered', handlePlayerAnswered);
      gameAPI.on('game_finished', handleGameFinished);
      gameAPI.on('player_surrendered', handlePlayerSurrendered);
      gameAPI.on('player_left', handlePlayerLeft);
      gameAPI.on('stage_completed', handleStageCompleted);
      gameAPI.on('category_selected', handleCategorySelected);
      gameAPI.on('help_used', handleHelpUsed);
      gameAPI.on('timeout', handleTimeout);
      gameAPI.on('error', handleError);
      
    } catch (err) {
      setError(err.message);
      setView(VIEWS.HOME);
    }
  };

  // ============================================
  // ۷. هندلرهای رویداد
  // ============================================
  
  const handleGameStarted = useCallback((data) => {
    if (!isMounted.current) return;
    setPlayers(data.players);
    setMyPlayerId(data.myId);
    setCurrentStage(0);
    setCurrentQuestionIndex(0);
    setScore(0);
    setOpponentScore(0);
    setIsAnswered(false);
    setHelpUsed({ remove_two: false, ask_audience: false, renewed_chance: false });
    setQuestionResult(null);
    setView(VIEWS.TOPIC_SELECT);
    setTemporaryCategories([]);
  }, []);

  const handleOpponentFound = useCallback((data) => {
    if (!isMounted.current) return;
    setWaitingOpponent(data.username);
  }, []);

  const handleNewQuestion = useCallback((data) => {
    if (!isMounted.current) return;
    setCurrentQuestion(data.question);
    setTimer(data.timer || QUESTION_TIMER);
    setIsAnswered(false);
    setCurrentStage(data.stage - 1);
    setCurrentQuestionIndex(data.questionNumber - 1);
    setHelpUsed({ remove_two: false, ask_audience: false, renewed_chance: false });
    setView(VIEWS.PLAYING);
  }, []);

  const handleTimerUpdate = useCallback((data) => {
    if (!isMounted.current) return;
    setTimer(data.timer);
  }, []);

  const handleAnswerResult = useCallback((data) => {
    if (!isMounted.current) return;
    setScore(prev => prev + data.points);
    setIsAnswered(true);
    setQuestionResult({
      correct: data.correct,
      correctAnswer: data.correctAnswer,
      points: data.points,
      yourScore: score + data.points,
    });
    setView(VIEWS.QUESTION_RESULT);
  }, [score]);

  const handlePlayerAnswered = useCallback((data) => {
    if (!isMounted.current) return;
    if (data.playerId !== myPlayerId) {
      setOpponentScore(data.score);
    }
  }, [myPlayerId]);

  const handleGameFinished = useCallback((data) => {
    if (!isMounted.current) return;
    setGameResult(data);
    setView(VIEWS.FINISHED);
    
    // به‌روزرسانی سکه و XP
    const myRank = data.rankings.find(r => r.id === myPlayerId);
    if (myRank && myRank.coins > 0) {
      addRewards(myRank.coins, myRank.xp);
    }
    
    // ذخیره در بازی‌های اخیر
    const myData = data.rankings.find(r => r.id === myPlayerId);
    if (myData) {
      saveRecentGame({
        opponent: data.rankings.find(r => r.id !== myPlayerId)?.username || 'حریف',
        result: myData.rank === 1 ? 'برد' : 'باخت',
        score: myData.score,
        time: new Date().toLocaleString('fa-IR'),
      });
    }
  }, [myPlayerId, addRewards]);

  const handlePlayerSurrendered = useCallback((data) => {
    if (!isMounted.current) return;
    // نمایش پیام در UI
    console.log(`بازیکن ${data.username} تسلیم شد`);
  }, []);

  const handlePlayerLeft = useCallback((data) => {
    if (!isMounted.current) return;
    console.log(`بازیکن ${data.username} خارج شد`);
  }, []);

  const handleStageCompleted = useCallback((data) => {
    if (!isMounted.current) return;
    setView(VIEWS.TOPIC_SELECT);
    setTemporaryCategories([]);
  }, []);

  const handleCategorySelected = useCallback((data) => {
    if (!isMounted.current) return;
    console.log(`موضوع ${data.category} انتخاب شد`);
  }, []);

  const handleHelpUsed = useCallback((data) => {
    if (!isMounted.current) return;
    if (data.type === 'remove_two') {
      setHelpUsed(prev => ({ ...prev, remove_two: true }));
    } else if (data.type === 'ask_audience') {
      setHelpUsed(prev => ({ ...prev, ask_audience: true }));
    } else if (data.type === 'renewed_chance') {
      setHelpUsed(prev => ({ ...prev, renewed_chance: true }));
    }
  }, []);

  const handleTimeout = useCallback((data) => {
    if (!isMounted.current) return;
    setIsAnswered(true);
    setQuestionResult({
      correct: false,
      correctAnswer: data.correctAnswer,
      points: 0,
      yourScore: score,
    });
    setView(VIEWS.QUESTION_RESULT);
  }, [score]);

  const handleError = useCallback((data) => {
    if (!isMounted.current) return;
    setError(data.message);
    setView(VIEWS.HOME);
  }, []);

  // ============================================
  // ۸. توابع اقدامات کاربر
  // ============================================
  
  const handleSelectCategory = (category) => {
    setTemporaryCategories(prev => {
      if (prev.includes(category)) {
        return prev.filter(c => c !== category);
      }
      if (prev.length >= 3) {
        return prev;
      }
      return [...prev, category];
    });
  };

  const handleConfirmCategories = () => {
    if (temporaryCategories.length !== 3) {
      alert('لطفاً دقیقاً ۳ موضوع انتخاب کنید');
      return;
    }
    setSelectedCategories(temporaryCategories);
    gameAPI.sendCategories(temporaryCategories);
  };

  const handleAnswer = (index) => {
    if (isAnswered) return;
    gameAPI.sendAnswer(index);
  };

  const handleUseHelp = (type) => {
    if (helpUsed[type]) return;
    const cost = HELP_COSTS[type];
    if ((coins || 0) < cost) {
      alert(`سکه کافی نیست! نیاز به ${cost} سکه دارید.`);
      return;
    }
    // کسر سکه
    addRewards(-cost, 0);
    gameAPI.sendHelp(type);
  };

  const handleSurrender = () => {
    if (window.confirm('آیا مطمئن هستید که می‌خواهید تسلیم شوید؟ (۲۰ سکه جریمه)')) {
      gameAPI.sendSurrender();
      setView(VIEWS.HOME);
    }
  };

  const handleCancelWaiting = () => {
    gameAPI.disconnect();
    setView(VIEWS.HOME);
  };

  const handleNewGame = () => {
    setGameResult(null);
    setCurrentQuestion(null);
    setScore(0);
    setOpponentScore(0);
    setView(VIEWS.HOME);
    gameAPI.disconnect();
  };

  const handleBackHome = () => {
    setView(VIEWS.HOME);
    gameAPI.disconnect();
  };

  // ============================================
  // ۹. پاکسازی هنگام unmount
  // ============================================
  
  useEffect(() => {
    return () => {
      clearInterval(timerIntervalRef.current);
      gameAPI.disconnect();
    };
  }, []);

  // ============================================
  // ۱۰. رندر صفحه خانه
  // ============================================
  
  const renderHome = () => (
    <div className="game-home">
      <div className="game-header-home">
        <div className="game-logo">
          <span className="logo-icon">👑</span>
          <span className="logo-text">Quiz of Kings</span>
        </div>
        <div className="user-stats">
          <div className="stat-item">
            <span className="stat-icon">⭐</span>
            <span className="stat-value">{xp || 0}</span>
          </div>
          <div className="stat-item">
            <span className="stat-icon">💰</span>
            <span className="stat-value">{coins || 0}</span>
          </div>
          <div className="stat-item">
            <span className="stat-icon">⚡</span>
            <span className="stat-value">{xp || 0}</span>
          </div>
        </div>
      </div>

      <div className="game-dashboard">
        <div className="dashboard-card">
          <div className="card-header">
            <span className="card-icon">🔥</span>
            <span className="card-title">چالش‌ها</span>
          </div>
          <div className="card-content">
            <span className="challenge-timer">تا اتمام ۳ روز ۱۸ ساعت</span>
          </div>
        </div>

        <div className="dashboard-card">
          <div className="card-header">
            <span className="card-icon">⭐</span>
            <span className="card-title">لیگ ستاره</span>
          </div>
          <div className="card-content">
            <span className="league-locked">🔒 باز شدن در سطح ۱۰</span>
          </div>
        </div>

        <div className="dashboard-card">
          <div className="card-header">
            <span className="card-icon">🏆</span>
            <span className="card-title">جام حذفی</span>
          </div>
          <div className="card-content">
            <span className="cup-subtitle">فقط یکی می‌مونه!</span>
          </div>
        </div>

        <div className="game-modes">
          <button className="mode-select-btn" onClick={() => startGame('random')}>
            🎲 حریف شانسی
          </button>
          <button className="mode-select-btn" onClick={() => startGame('friends')}>
            👫 لیگ دوستانه
          </button>
          <button className="mode-select-btn" onClick={() => startGame('stars')}>
            ⭐ لیگ ستاره‌ها
          </button>
          <button className="mode-select-btn" onClick={() => startGame('record')}>
            🏆 حالت رکورد
          </button>
        </div>

        <button className="btn-start-game" onClick={() => startGame('random')}>
          <span className="btn-text">شروع بازی جدید</span>
          <span className="btn-icon">▶</span>
        </button>
      </div>

      {recentGames.length > 0 && (
        <div className="recent-games">
          <h3 className="recent-title">بازی‌های اخیر</h3>
          {recentGames.map((game, idx) => (
            <div key={idx} className="recent-game-item">
              <div className="game-info">
                <span className="opponent-name">{game.opponent}</span>
                <span className={`game-result ${game.result === 'برد' ? 'win' : 'lose'}`}>
                  {game.result}
                </span>
              </div>
              <div className="game-meta">
                <span className="game-score">امتیاز: {game.score}</span>
                <span className="game-time">{game.time}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {error && <div className="error-message">{error}</div>}
    </div>
  );

  // ============================================
  // ۱۱. رندر صفحه انتظار
  // ============================================
  
  const renderWaiting = () => (
    <div className="game-waiting">
      <div className="waiting-content">
        <div className="waiting-spinner"></div>
        <h2 className="waiting-title">در جستجوی حریف ...</h2>
        <p className="waiting-user">{(user?.username || 'کاربر')}</p>
        <p className="waiting-mode">
          {mode === 'random' && '🎲 حریف شانسی'}
          {mode === 'friends' && '👫 لیگ دوستانه'}
          {mode === 'stars' && '⭐ لیگ ستاره‌ها'}
          {mode === 'record' && '🏆 حالت رکورد'}
        </p>
        {waitingOpponent && (
          <p className="waiting-opponent">✅ حریف پیدا شد: {waitingOpponent}</p>
        )}
        <button className="btn-cancel" onClick={handleCancelWaiting}>
          لغو بازی
        </button>
      </div>
    </div>
  );

  // ============================================
  // ۱۲. رندر انتخاب موضوع
  // ============================================
  
  const renderTopicSelect = () => {
    const opponent = players.find(p => p.id !== myPlayerId);
    return (
      <div className="game-topic-select">
        <div className="topic-modal">
          <h2 className="topic-title">
            موضوع خود را مقابل {opponent?.username || 'حریف'} مشخص کنید
          </h2>
          <p className="topic-subtitle">می‌خوای موضوعات پیشنهادی رو تغییر بدی؟</p>
          <div className="topic-grid">
            {CATEGORIES.map((cat, idx) => (
              <button
                key={idx}
                className={`topic-item ${temporaryCategories.includes(cat) ? 'selected' : ''}`}
                onClick={() => handleSelectCategory(cat)}
              >
                <span className="topic-icon">📚</span>
                <span className="topic-name">{cat}</span>
                {temporaryCategories.includes(cat) && (
                  <span className="topic-check">✓</span>
                )}
              </button>
            ))}
          </div>
          <div className="topic-actions">
            <span className="topic-count">
              {temporaryCategories.length} از ۳ انتخاب شده
            </span>
            <button
              className="btn-confirm-topics"
              onClick={handleConfirmCategories}
              disabled={temporaryCategories.length !== 3}
            >
              تأیید موضوعات
            </button>
          </div>
        </div>
      </div>
    );
  };

  // ============================================
  // ۱۳. رندر صفحه بازی
  // ============================================
  
  const renderPlaying = () => {
    if (!currentQuestion) return <div className="loading">بارگذاری...</div>;
    
    const opponent = players.find(p => p.id !== myPlayerId);
    const timerPercent = (timer / QUESTION_TIMER) * 100;
    const timerColor = timerPercent > 50 ? '#4ade80' : timerPercent > 25 ? '#facc15' : '#f87171';
    
    return (
      <div className="game-playing">
        <div className="game-header-playing">
          <div className="score-display">
            <div className="score-opponent">
              <span className="score-label">{opponent?.username || 'حریف'}</span>
              <span className="score-value">{opponentScore}</span>
            </div>
            <div className="score-vs">⚔️</div>
            <div className="score-player">
              <span className="score-label">{user?.username || 'شما'}</span>
              <span className="score-value">{score}</span>
            </div>
          </div>
          <div className="game-progress">
            <span className="stage-info">
              مرحله {currentStage + 1} از {TOTAL_STAGES}
            </span>
            <span className="question-info">
              سوال {currentQuestionIndex + 1} از {QUESTIONS_PER_STAGE}
            </span>
          </div>
        </div>

        <div className="question-container">
          <div className="question-category-badge">
            {currentQuestion.category}
          </div>
          <div className="question-timer">
            <svg className="timer-circle" viewBox="0 0 36 36">
              <path
                className="timer-bg"
                d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
              />
              <path
                className="timer-progress"
                stroke={timerColor}
                strokeDasharray={`${timerPercent}, 100`}
                d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
              />
              <text x="18" y="20.35" className="timer-text">{timer}</text>
            </svg>
          </div>
          <div className="question-text">
            {currentQuestion.image && (
              <img src={currentQuestion.image} alt="سوال" className="question-image" />
            )}
            <p>{currentQuestion.question}</p>
          </div>
          <div className="question-options">
            {currentQuestion.options.map((option, idx) => {
              const letters = ['الف', 'ب', 'ج', 'د'];
              const isDisabled = isAnswered;
              const isCorrect = isAnswered && idx === currentQuestion.correctAnswer;
              const isWrong = isAnswered && idx !== currentQuestion.correctAnswer && helpUsed.renewed_chance;
              
              return (
                <button
                  key={idx}
                  className={`option-button ${isDisabled ? 'disabled' : ''} ${isCorrect ? 'correct' : ''} ${isWrong ? 'wrong' : ''}`}
                  onClick={() => handleAnswer(idx)}
                  disabled={isDisabled}
                >
                  <span className="option-letter">{letters[idx]}</span>
                  <span className="option-text">{option}</span>
                  {isCorrect && <span className="option-check">✅</span>}
                  {isWrong && <span className="option-check">❌</span>}
                </button>
              );
            })}
          </div>
        </div>

        <div className="help-buttons">
          <button
            className={`help-btn ${helpUsed.remove_two ? 'used' : ''}`}
            onClick={() => handleUseHelp('remove_two')}
            disabled={helpUsed.remove_two || isAnswered}
          >
            ❌ حذف دو گزینه
            <span className="help-cost">💰 {HELP_COSTS.remove_two}</span>
          </button>
          <button
            className={`help-btn ${helpUsed.ask_audience ? 'used' : ''}`}
            onClick={() => handleUseHelp('ask_audience')}
            disabled={helpUsed.ask_audience || isAnswered}
          >
            👥 جواب‌های مردم
            <span className="help-cost">💰 {HELP_COSTS.ask_audience}</span>
          </button>
          <button
            className={`help-btn ${helpUsed.renewed_chance ? 'used' : ''}`}
            onClick={() => handleUseHelp('renewed_chance')}
            disabled={helpUsed.renewed_chance || isAnswered}
          >
            🔄 شانس مجدد
            <span className="help-cost">💰 {HELP_COSTS.renewed_chance}</span>
          </button>
          <button className="help-btn surrender" onClick={handleSurrender}>
            🏳️ تسلیم
          </button>
        </div>
      </div>
    );
  };

  // ============================================
  // ۱۴. رندر نتیجه سوال
  // ============================================
  
  const renderQuestionResult = () => {
    if (!questionResult) return null;
    const letters = ['الف', 'ب', 'ج', 'د'];
    
    return (
      <div className="question-result-overlay">
        <div className={`result-card ${questionResult.correct ? 'correct' : 'wrong'}`}>
          <div className="result-icon">
            {questionResult.correct ? '✅' : '❌'}
          </div>
          <div className="result-text">
            {questionResult.correct
              ? `پاسخ صحیح! +${questionResult.points} امتیاز`
              : `پاسخ اشتباه! پاسخ صحیح: گزینه ${letters[questionResult.correctAnswer]}`}
          </div>
          <div className="result-score">
            امتیاز شما: {questionResult.yourScore}
          </div>
          <div className="result-actions">
            <button className="result-btn like">👍 باحال بود</button>
            <button className="result-btn dislike">👎 حال نکردم</button>
          </div>
        </div>
      </div>
    );
  };

  // ============================================
  // ۱۵. رندر پایان بازی
  // ============================================
  
  const renderFinished = () => {
    if (!gameResult) return null;
    
    const myData = gameResult.rankings.find(r => r.id === myPlayerId);
    const winner = gameResult.rankings.find(r => r.rank === 1);
    
    return (
      <div className="game-finished">
        <div className="finished-header">
          <h2>🏁 بازی تمام شد!</h2>
          <div className="winner-display">
            🏆 برنده: {winner?.username}
            {winner?.id === myPlayerId && ' 👑 شما!'}
          </div>
          <div className="reward-summary">
            {myData && (
              <>
                <span>💰 +{myData.coins} سکه</span>
                <span>⚡ +{myData.xp} XP</span>
              </>
            )}
          </div>
        </div>
        <div className="rankings-table-container">
          <table className="rankings-table">
            <thead>
              <tr>
                <th>رتبه</th>
                <th>بازیکن</th>
                <th>امتیاز</th>
                <th>سکه</th>
                <th>XP</th>
              </tr>
            </thead>
            <tbody>
              {gameResult.rankings.map((player, idx) => (
                <tr key={idx} className={player.id === myPlayerId ? 'highlight' : ''}>
                  <td>{player.rank}</td>
                  <td>{player.username}</td>
                  <td>{player.score}</td>
                  <td>+{player.coins}</td>
                  <td>+{player.xp}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="finished-actions">
          <button className="btn-new-game" onClick={handleNewGame}>
            بازی جدید
          </button>
          <button className="btn-back-home" onClick={handleBackHome}>
            بازگشت
          </button>
        </div>
      </div>
    );
  };

  // ============================================
  // ۱۶. رندر اصلی
  // ============================================
  
  return (
    <div className="game-container">
      {view === VIEWS.HOME && renderHome()}
      {view === VIEWS.WAITING && renderWaiting()}
      {view === VIEWS.TOPIC_SELECT && renderTopicSelect()}
      {view === VIEWS.PLAYING && renderPlaying()}
      {view === VIEWS.QUESTION_RESULT && renderQuestionResult()}
      {view === VIEWS.FINISHED && renderFinished()}
    </div>
  );
};

export default Game;